/**
 * AssetApprovalChainControl — single-asset approval chain assign/change/remove control
 *
 *
 * Lives in the asset detail sidebar's Approval view. Behaviour:
 *   - `canManage` users get a Select listing every project approval chain plus a
 *     "No approval chain" option. Choosing an approval chain assigns/changes it; choosing
 *     "No approval chain" while one is assigned opens a small confirm, then removes.
 *   - Read-only users see the assigned approval chain name as text (or nothing when
 *     there is no approval chain — no affordance to add one).
 *
 * Error UX is owned here (the mutations suppress the global toast): failures
 * surface via `notificationService` + `parseProblemDetail`.
 */
import { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@monksflow/monks-ui';
import { LockIcon, XIcon } from 'lucide-react';

import { useAssetApprovalChain } from '@/entities/asset-approval-chain';
import { useApprovalChains } from '@/entities/approval-chain';
import { notificationService } from '@/lib/services/notification.service';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal';
import { parseProblemDetail } from '@/shared/utils/problem-details';

import { useAssignAssetApprovalChain } from '../../api/useAssignAssetApprovalChain.mutation';
import { useRemoveAssetApprovalChain } from '../../api/useRemoveAssetApprovalChain.mutation';
import { useTranslation } from 'react-i18next';

/** Sentinel Select value for the "No approval chain" option (Select needs a string). */
// eslint-disable-next-line i18next/no-literal-string -- sentinel Select option value
const NO_APPROVAL_CHAIN_VALUE = '__none__';

export interface AssetApprovalChainControlProps {
  projectId: string;
  fileId: string;
  /** Folder in view — threaded to the mutations so the files list refreshes. */
  folderId?: string;
  /** Holds `project:manage-approval-chains` — gates assign/change/remove. */
  canManage: boolean;
}

export function AssetApprovalChainControl({
  projectId,
  fileId,
  folderId,
  canManage,
}: AssetApprovalChainControlProps) {
  const { t } = useTranslation('projects');

  const { data: assignment, isLoading: isLoadingAssignment } = useAssetApprovalChain(
    projectId,
    fileId,
  );
  const { data: approvalChainsResponse, isLoading: isLoadingApprovalChains } =
    useApprovalChains(projectId);
  const approvalChains = approvalChainsResponse?.items ?? [];

  const assignApprovalChain = useAssignAssetApprovalChain({ projectId, folderId });
  const removeApprovalChain = useRemoveAssetApprovalChain({ projectId, folderId });

  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false);
  const [approvalChainPendingSwitch, setApprovalChainPendingSwitch] = useState<string | null>(null);

  const assignedId = assignment?.workflowDefinition.id ?? null;
  const assignedName = assignment?.workflowDefinition.name ?? null;
  const isMutating = assignApprovalChain.isPending || removeApprovalChain.isPending;

  const pendingSwitchName =
    approvalChains.find((approvalChain) => approvalChain.id === approvalChainPendingSwitch)?.name ??
    '';

  const assignApprovalChainById = (approvalChainId: string) => {
    assignApprovalChain.mutate(
      { assetId: fileId, workflowDefinitionId: approvalChainId },
      {
        onSuccess: () => {
          setApprovalChainPendingSwitch(null);
          notificationService.showSuccess(
            t('assignAssetApprovalChain.assetApprovalChainControl.approvalChainAssigned'),
          );
        },
        onError: (error) => {
          setApprovalChainPendingSwitch(null);
          const { detail } = parseProblemDetail(
            error,
            t('assignAssetApprovalChain.assetApprovalChainControl.failedToAssignApprovalChain'),
          );
          notificationService.showError(detail);
        },
      },
    );
  };

  const handleSelect = (value: string) => {
    if (value === NO_APPROVAL_CHAIN_VALUE) {
      if (assignedId) setIsConfirmingRemove(true);
      return;
    }
    if (value === assignedId) return; // no-op re-select

    if (assignedId) {
      setApprovalChainPendingSwitch(value);
      return;
    }

    assignApprovalChainById(value);
  };

  const handleConfirmRemove = () => {
    removeApprovalChain.mutate(fileId, {
      onSuccess: () => {
        setIsConfirmingRemove(false);
        notificationService.showSuccess(
          t('assignAssetApprovalChain.assetApprovalChainControl.approvalChainRemoved'),
        );
      },
      onError: (error) => {
        const { detail } = parseProblemDetail(
          error,
          t('assignAssetApprovalChain.assetApprovalChainControl.failedToRemoveApprovalChain'),
        );
        notificationService.showError(detail);
      },
    });
  };

  if (!canManage && !assignedName) return null;

  return (
    <div className="flex w-full items-center gap-1" data-testid="asset-approval-chain-control">
      {canManage ? (
        <Select
          // Radix shows the placeholder only for an empty value, and the sentinel has no item
          // until a chain is assigned, so it would leave the trigger blank.
          value={assignedId ?? ''}
          onValueChange={handleSelect}
          disabled={isLoadingAssignment || isLoadingApprovalChains || isMutating}
        >
          <SelectTrigger
            size="lg"
            className="min-w-48 flex-1"
            data-testid="asset-approval-chain-select"
          >
            <SelectValue
              // Empty until the assignment lands, so an asset with a chain never reads as one without.
              placeholder={
                isLoadingAssignment
                  ? undefined
                  : t('assignAssetApprovalChain.assetApprovalChainControl.noApprovalChain')
              }
            />
          </SelectTrigger>
          <SelectContent>
            {approvalChains.map((approvalChain) => (
              <SelectItem key={approvalChain.id} value={approvalChain.id}>
                {approvalChain.name}
              </SelectItem>
            ))}
            {assignedId && (
              <>
                <SelectSeparator />
                <SelectItem value={NO_APPROVAL_CHAIN_VALUE}>
                  <XIcon className="size-4" aria-hidden />
                  {t('assignAssetApprovalChain.assetApprovalChainControl.clear')}
                </SelectItem>
              </>
            )}
          </SelectContent>
        </Select>
      ) : (
        <div
          className="flex items-center justify-between gap-1 rounded-md w-full bg-muted p-2 text-xs border border-surface-secondary"
          data-testid="asset-approval-chain-control-readonly"
        >
          <p className="text-foreground text-body-md font-medium">{assignedName}</p>
          <div className="flex items-center gap-1">
            <LockIcon className="size-3.5 text-foreground-subtle" />
            <p className="text-foreground-subtle text-body-sm">
              {t('assignAssetApprovalChain.assetApprovalChainControl.editPermissionOnly')}
            </p>
          </div>
        </div>
      )}

      {isMutating && <Spinner className="size-4" />}

      <DeleteConfirmationModal
        open={approvalChainPendingSwitch !== null}
        isDeleting={assignApprovalChain.isPending}
        customModalTitle={t(
          'assignAssetApprovalChain.assetApprovalChainControl.changeApprovalChain',
        )}
        warningMessage={t(
          'assignAssetApprovalChain.assetApprovalChainControl.applyingNamedApprovalChainClears',
          { name: pendingSwitchName },
        )}
        confirmLabel={t(
          'assignAssetApprovalChain.assetApprovalChainControl.confirmChangeApprovalChain',
        )}
        pendingLabel={t('assignAssetApprovalChain.assetApprovalChainControl.changingApprovalChain')}
        onCancel={() => setApprovalChainPendingSwitch(null)}
        onConfirmDelete={() => {
          if (approvalChainPendingSwitch) assignApprovalChainById(approvalChainPendingSwitch);
        }}
        data-testid="change-asset-approval-chain-modal"
      />

      <DeleteConfirmationModal
        open={isConfirmingRemove}
        isDeleting={removeApprovalChain.isPending}
        customModalTitle={t(
          'assignAssetApprovalChain.assetApprovalChainControl.removeApprovalChain',
        )}
        warningMessage={
          assignedName
            ? t(
                'assignAssetApprovalChain.assetApprovalChainControl.thisRemovesNamedApprovalChainFrom',
                {
                  name: assignedName,
                },
              )
            : t(
                'assignAssetApprovalChain.assetApprovalChainControl.thisRemovesTheApprovalChainFrom',
              )
        }
        confirmLabel={t(
          'assignAssetApprovalChain.assetApprovalChainControl.confirmRemoveApprovalChain',
        )}
        onCancel={() => setIsConfirmingRemove(false)}
        onConfirmDelete={handleConfirmRemove}
        data-testid="remove-asset-approval-chain-modal"
      />
    </div>
  );
}

AssetApprovalChainControl.displayName = 'AssetApprovalChainControl';
